import { useParams } from 'react-router-dom';
import { useState, useCallback } from 'react';
import { Spin, Breadcrumb, message, Form, Input, Alert, Radio } from 'antd';
import PageMeta from 'components/PageMeta/PageMeta';
import PageTitleBar from 'components/PageTitleBar/PageTitleBar';
import { useAsync } from 'react-async-hook';
import { providerService } from 'services';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import Card, { CardEdit } from 'components/Card/Card';
import { useEditCard, useUnsavedChangesGuard } from 'components/Card/useEditCard';
import TabularList from 'components/TabularList/TabularList';
import { EMDASH, ROLES } from 'constants';
import PhoneNumber from 'components/PhoneNumber';
import PhoneNumberContainer from 'components/PhoneNumberContainer';
import FormItem from 'components/FormItem';
import { getPhoneNumberValidator, getPhoneEXTValidator, getFormattedPhoneNumber, hasPermission } from 'utils';
import { useAddressFieldsData } from 'components/AddressFields/AddressFieldsFlat';
import RadioCards from 'components/RadioCards/RadioCards';
import BankingProfiles from 'containers/Provider/v2/BusinessProfile/__components/BankingProfiles';
import TaxProfiles from 'containers/Provider/v2/BusinessProfile/__components/TaxProfiles';
import Locations from './Locations';
import { useSelector } from 'react-redux';
import { selectRoles } from 'features/auth';

const ProviderOrganization = () => {
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [businessForm] = Form.useForm();
  const [contactForm] = Form.useForm();
  const [careFacilities, setCareFacilities] = useState(null);
  const [careFacilitiesVersion, setCareFacilitiesVersion] = useState(0);
  const [batchUpdateMode, setBatchUpdateMode] = useState('all');
  const [selectedLocations, setSelectedLocations] = useState([]);

  const refetchCareFacilities = useCallback(() => setCareFacilitiesVersion((v) => v + 1), []);

  const fetchOrganization = useCallback(() => {
    setLoading(true);
    providerService
      .getOrganization(id)
      .then((data) => {
        setOrganization(data);
      })
      .catch((error) => {
        console.error('Error fetching organization', error);
        message.error('Unable to fetch organization details');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  useAsync(fetchOrganization, [id]);

  useAsync(async () => {
    try {
      const result = await providerService.getCareFacilities(id);
      const facilities = result?.content ?? result ?? [];

      const applications = await Promise.all(
        facilities.map((facility) => providerService.getCarefacilityApplications(facility.id).catch(() => [])),
      );

      facilities.forEach((facility, index) => {
        facility.applications = applications[index] ?? [];
      });

      setCareFacilities(facilities);
    } catch (error) {
      console.error('Error fetching care facilities', error);
      message.error('Unable to fetch locations');
    }
  }, [id, careFacilitiesVersion]);

  const { isEditing: isBusinessEditing, editProps: businessEditProps } = useEditCard({
    id: 'org-details',
    onEditStart: () => {
      businessForm.setFieldsValue({
        businessLegalName: organization?.businessLegalName,
        address: organization?.address,
        businessPhone: organization?.businessPhone,
        businessPhoneExt: organization?.businessPhoneExt,
      });
      setBatchUpdateMode('all');
      setSelectedLocations([]);
    },
    onSave: async () => {
      await businessForm.validateFields();
      const values = businessForm.getFieldsValue();

      const payload = {
        businessLegalName: values.businessLegalName,
        address: values.address,
        businessPhone: values.businessPhone,
        businessPhoneExt: values.businessPhoneExt,
      };

      const careFacilityIds = batchUpdateMode === 'selected' && selectedLocations.length > 0 ? selectedLocations : [];

      const updatedOrg = await providerService.batchUpdateOrganization(id, payload, careFacilityIds);
      setOrganization(updatedOrg);
      businessForm.resetFields();
      setBatchUpdateMode('all');
      setSelectedLocations([]);

      message.success('Organization details updated');
    },
    onDiscard: () => {
      businessForm.resetFields();
      setBatchUpdateMode('all');
      setSelectedLocations([]);
    },
  });

  const { isEditing: isContactEditing, editProps: contactEditProps } = useEditCard({
    id: 'org-contact',
    onEditStart: () =>
      contactForm.setFieldsValue({
        firstName: organization?.businessContact?.firstName,
        lastName: organization?.businessContact?.lastName,
        position: organization?.businessContact?.position,
        phone: organization?.businessContact?.phone,
        phoneExt: organization?.businessContact?.phoneExt,
        email: organization?.businessContact?.email,
      }),
    onSave: async () => {
      await contactForm.validateFields();
      const values = contactForm.getFieldsValue();
      const updatedOrg = await providerService.updateOrganization(id, {
        businessContact: {
          firstName: values.firstName,
          lastName: values.lastName,
          position: values.position,
          phone: values.phone,
          phoneExt: values.phoneExt,
          email: values.email,
        },
      });
      setOrganization(updatedOrg);
      contactForm.resetFields();
      message.success('Organization contact updated');
    },
    onDiscard: () => contactForm.resetFields(),
  });

  // Intercept page-leave when any card is being edited
  useUnsavedChangesGuard();

  const userRoles = useSelector(selectRoles);
  const canEditPCSContent = hasPermission(userRoles, [ROLES.PROVIDER_MANAGER, ROLES.PROVIDER_COORDINATOR]);

  // Address fields data for Organization Details
  const addressFieldsData = useAddressFieldsData({
    form: businessForm,
    addressKey: 'address',
    isEditing: isBusinessEditing,
    addressValue: organization?.address,
    disabled: businessEditProps.loading,
  });

  return (
    <Spin size="large" spinning={loading}>
      <div className="min-h-screen">
        <PageMeta title={organization?.businessLegalName || 'Organization'} />

        <Breadcrumb
          className="mb-6 pt-6 px-6 md:px-0 md:pt-0 md:pb-4"
          items={[
            {
              title: 'Provider Queue',
              href: '/providers',
              onClick: (e) => {
                e.preventDefault();
                history.push('/providers');
              },
            },
            {
              title: organization?.businessLegalName || 'Organization',
            },
          ]}
        />

        <PageTitleBar
          title={organization?.businessLegalName || 'Organization'}
          subtitle={<div className="flex items-center gap-3 flex-wrap">Organization #{organization?.id ?? ''}</div>}
          noChildrenPadding
        >
          <div className="py-4 px-8">
            {careFacilities ? (
              <div className="text-sm text-white/70">
                {careFacilities?.length ?? 0} location{careFacilities?.length !== 1 ? 's' : ''} ·{' '}
                {careFacilities?.reduce((total, facility) => total + (facility.applications?.length || 0), 0) ?? 0}{' '}
                application
                {careFacilities?.reduce((total, facility) => total + (facility.applications?.length || 0), 0) !== 1
                  ? 's'
                  : ''}
              </div>
            ) : (
              <Spin size="small" />
            )}
          </div>
        </PageTitleBar>

        {/* Main Section */}
        <div className="flex flex-col lg:flex-row gap-4 mt-8 px-6 md:px-0">
          <div className="lg:w-1/2 w-full">
            <Card
              title="Organization Details"
              extra={canEditPCSContent ? <CardEdit {...businessEditProps} /> : null}
              stickyHeader
              noBodyPadding
            >
              <Form form={businessForm} disabled={businessEditProps.loading} layout="vertical">
                {isBusinessEditing && careFacilities && careFacilities.length > 0 && (
                  <Alert
                    type="info"
                    message="Apply changes to:"
                    description={
                      <div className="flex flex-col gap-4">
                        <Radio.Group
                          value={batchUpdateMode}
                          onChange={(e) => {
                            setBatchUpdateMode(e.target.value);
                            if (e.target.value === 'all') {
                              setSelectedLocations([]);
                              businessForm.setFieldValue('selectedLocations', []);
                            } else {
                              businessForm.setFieldValue('selectedLocations', selectedLocations);
                            }
                          }}
                          className="flex flex-col gap-2"
                        >
                          <Radio value="all">
                            All {careFacilities.length} location{careFacilities.length !== 1 ? 's' : ''}
                          </Radio>
                          <Radio value="selected">Selected locations only</Radio>
                        </Radio.Group>

                        {batchUpdateMode === 'selected' && (
                          <div className="ml-6 pl-1 border-l-2 border-gray-200">
                            <Form.Item
                              name="selectedLocations"
                              rules={[
                                {
                                  validator: (_, value) => {
                                    if (batchUpdateMode === 'selected' && (!value || value.length === 0)) {
                                      return Promise.reject(new Error('Please select at least one location'));
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                              className="mb-0"
                            >
                              <RadioCards
                                multiple
                                size="small"
                                spacing="small"
                                value={selectedLocations}
                                onChange={(value) => {
                                  setSelectedLocations(value);
                                  businessForm.setFieldValue('selectedLocations', value);
                                }}
                                options={careFacilities.map((facility) => ({
                                  value: facility.id,
                                  label: facility.displayName || `Location ${facility.id}`,
                                }))}
                              />
                            </Form.Item>
                          </div>
                        )}
                      </div>
                    }
                    className="m-6 mb-0"
                  />
                )}
                <TabularList
                  indented
                  data={[
                    {
                      title: 'Legal Name',
                      titleRequired: true,
                      value: isBusinessEditing ? (
                        <Form.Item
                          name="businessLegalName"
                          className="mb-0"
                          rules={[{ required: true, message: 'Legal name is required' }]}
                        >
                          <Input placeholder="Enter legal name" />
                        </Form.Item>
                      ) : (
                        organization?.businessLegalName
                      ),
                    },
                    ...addressFieldsData,
                    {
                      title: 'Phone',
                      titleRequired: true,
                      value: isBusinessEditing ? (
                        <PhoneNumberContainer
                          phoneNumber={
                            <FormItem
                              name="businessPhone"
                              label="Phone number"
                              rules={[
                                { required: true, message: 'Phone number is required' },
                                getPhoneNumberValidator('Invalid Phone Number'),
                              ]}
                              className="mb-0"
                            >
                              <PhoneNumber aria-label="phone number" disabled={businessEditProps.loading} />
                            </FormItem>
                          }
                          extension={
                            <FormItem
                              name="businessPhoneExt"
                              label="Extension"
                              rules={[getPhoneEXTValidator('Invalid Extension')]}
                              className="mb-0"
                            >
                              <Input aria-label="phone extension" />
                            </FormItem>
                          }
                        />
                      ) : (
                        <>
                          {getFormattedPhoneNumber(organization?.businessPhone)}
                          {organization?.businessPhoneExt && ` Ext ${organization?.businessPhoneExt}`}
                        </>
                      ),
                    },
                  ]}
                />
              </Form>
            </Card>
          </div>

          <div className="lg:w-1/2 w-full">
            <Card
              title="Organization Contact"
              extra={canEditPCSContent ? <CardEdit {...contactEditProps} /> : null}
              stickyHeader
              noBodyPadding
            >
              <TabularList
                indented
                data={[
                  {
                    title: 'Name',
                    titleRequired: true,
                    value: isContactEditing ? (
                      <Form form={contactForm} disabled={contactEditProps.loading} layout="vertical">
                        <div className="grid grid-cols-2 gap-2">
                          <Form.Item
                            name="firstName"
                            className="mb-0"
                            rules={[{ required: true, message: 'First name is required' }]}
                          >
                            <Input placeholder="First name" />
                          </Form.Item>
                          <Form.Item
                            name="lastName"
                            className="mb-0"
                            rules={[{ required: true, message: 'Last name is required' }]}
                          >
                            <Input placeholder="Last name" />
                          </Form.Item>
                        </div>
                      </Form>
                    ) : (
                      `${organization?.businessContact?.firstName || ''} ${
                        organization?.businessContact?.lastName || ''
                      }`.trim() || EMDASH
                    ),
                  },
                  {
                    title: 'Role / Position',
                    titleRequired: true,
                    value: isContactEditing ? (
                      <Form form={contactForm} disabled={contactEditProps.loading} layout="vertical">
                        <Form.Item name="position" className="mb-0">
                          <Input placeholder="Enter role or position" />
                        </Form.Item>
                      </Form>
                    ) : (
                      organization?.businessContact?.position || EMDASH
                    ),
                  },
                  {
                    title: 'Phone',
                    titleRequired: true,
                    value: isContactEditing ? (
                      <Form form={contactForm} disabled={contactEditProps.loading} layout="vertical">
                        <PhoneNumberContainer
                          phoneNumber={
                            <FormItem
                              name="phone"
                              label="Phone number"
                              rules={[
                                { required: true, message: 'Phone number is required' },
                                getPhoneNumberValidator('Invalid Phone Number'),
                              ]}
                              className="mb-0"
                            >
                              <PhoneNumber aria-label="phone number" disabled={contactEditProps.loading} />
                            </FormItem>
                          }
                          extension={
                            <FormItem
                              name="phoneExt"
                              label="Extension"
                              rules={[getPhoneEXTValidator('Invalid Extension')]}
                              className="mb-0"
                            >
                              <Input aria-label="phone extension" />
                            </FormItem>
                          }
                        />
                      </Form>
                    ) : (
                      <>
                        {getFormattedPhoneNumber(organization?.businessContact?.phone)}
                        {organization?.businessContact?.phoneExt && ` Ext ${organization?.businessContact?.phoneExt}`}
                      </>
                    ),
                  },
                  {
                    title: 'Email',
                    titleRequired: true,
                    value: isContactEditing ? (
                      <Form form={contactForm} disabled={contactEditProps.loading} layout="vertical">
                        <Form.Item
                          name="email"
                          className="mb-0"
                          rules={[
                            { required: true, message: 'Email is required' },
                            { type: 'email', message: 'Please enter a valid email' },
                          ]}
                        >
                          <Input placeholder="Enter email address" />
                        </Form.Item>
                      </Form>
                    ) : (
                      organization?.businessContact?.email
                    ),
                  },
                ]}
              />
            </Card>
          </div>
        </div>

        {/* Banking Profiles */}
        <div className="mt-8 px-6 md:px-0">
          <BankingProfiles organization={organization} isEditable={canEditPCSContent} />
        </div>

        {/* Tax Profiles */}
        <div className="mt-8 px-6 md:px-0">
          <TaxProfiles organization={organization} careFacilities={careFacilities} isEditable={canEditPCSContent} />
        </div>

        {/* Locations */}
        <div className="mt-8 px-6 md:px-0">
          <Locations
            careFacilities={careFacilities}
            orgId={organization?.id}
            onRefetch={refetchCareFacilities}
            isEditable={canEditPCSContent}
          />
        </div>
      </div>
    </Spin>
  );
};

export default ProviderOrganization;
